import React from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Grid,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  FullscreenExit,
  AttachFile,
  Visibility,
  Description,
  Image,
  InsertDriveFile,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import useViewAttachments from '../../../hooks/purchaseInvoices/pages/useViewAttachments';
import {
  paperMain,
  paperInfo,
  paperAttachments,
  attachmentsList,
  attachmentItem,
  attachmentViewer,
  viewerToolbar,
  viewerContainer,
  viewerPager,
  noAttachmentMessage,
  attachmentIcon,
} from '../../../styles/purchaseInvoices/pages/viewAttachmentsStyle';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const ViewAttachments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    invoice,
    attachments,
    selectedAttachment,
    attachmentContent,
    loading,
    error,
    isFullscreen,
    scale,
    pageNumber,
    numPages,
    pdfError,
    setPageNumber,
    handleSelectAttachment,
    handleDownloadAttachment,
    handleLogin,
    handleBackToSystem,
    handleBackToInvoice,
    handleZoomIn,
    handleZoomOut,
    toggleFullscreen,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    getAttachmentSrc,
    getFileTypeIcon,
    isPdfFile,
  } = useViewAttachments();

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            טוען צרופות...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleLogin}>
            התחבר למערכת
          </Button>
        </Box>
      </Container>
    );
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'לא ידוע';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const renderAttachmentViewer = () => {
    if (!selectedAttachment || !attachmentContent) {
      return (
        <Box sx={noAttachmentMessage}>
          <Typography variant="h3" sx={attachmentIcon}>
            📎
          </Typography>
          <Typography variant="h6" gutterBottom>
            בחר צרופה לצפייה
          </Typography>
          <Typography variant="body2">
            בחר אחת מהצרופות מהרשימה לצפייה ועריכה
          </Typography>
        </Box>
      );
    }

    const isPDF = isPdfFile(selectedAttachment.originalFileName || selectedAttachment.name);
    
    if (pdfError && isPDF) {
      return (
        <Box sx={noAttachmentMessage}>
          <Typography variant="h3" sx={attachmentIcon}>
            ❌
          </Typography>
          <Typography variant="h6" gutterBottom color="error">
            שגיאה בטעינת הקובץ
          </Typography>
          <Typography variant="body2">
            לא ניתן להציג את הקובץ. נסה להוריד אותו במקום זאת.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => handleDownloadAttachment(selectedAttachment)}
            sx={{ mt: 2 }}
            startIcon={<Download />}
          >
            הורד קובץ
          </Button>
        </Box>
      );
    }

    if (isPDF) {
      const pdfSrc = getAttachmentSrc();
      if (pdfSrc && !pdfError) {
        return (
          <Document
            file={pdfSrc}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            error={
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  שגיאה בטעינת קובץ PDF
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => handleDownloadAttachment(selectedAttachment)}
                  sx={{ mt: 2 }}
                >
                  הורד קובץ PDF
                </Button>
              </Box>
            }
            loading={
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  טוען PDF...
                </Typography>
              </Box>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={<CircularProgress size={30} />}
            />
          </Document>
        );
      } else {
        return (
          <Box sx={noAttachmentMessage}>
            <Typography variant="h3" sx={attachmentIcon}>
              ❌
            </Typography>
            <Typography variant="h6" gutterBottom color="error">
              שגיאה בטעינת הקובץ
            </Typography>
            <Typography variant="body2">
              לא ניתן להציג את הקובץ. נסה להוריד אותו במקום זאת.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => handleDownloadAttachment(selectedAttachment)}
              sx={{ mt: 2 }}
              startIcon={<Download />}
            >
              הורד קובץ
            </Button>
          </Box>
        );
      }
    } else {
      // For non-PDF files, show a preview or download option
      const fileExtension = (selectedAttachment.originalFileName || selectedAttachment.name).split('.').pop()?.toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExtension);
      
      if (isImage) {
        // For images, we need to determine the MIME type and create a proper data URL
        const fileExtension = (selectedAttachment.originalFileName || selectedAttachment.name).split('.').pop()?.toLowerCase();
        let mimeType = 'image/jpeg'; // default
        switch (fileExtension) {
          case 'png': mimeType = 'image/png'; break;
          case 'gif': mimeType = 'image/gif'; break;
          case 'bmp': mimeType = 'image/bmp'; break;
          case 'webp': mimeType = 'image/webp'; break;
          default: mimeType = 'image/jpeg';
        }
        
        let imageSrc = attachmentContent;
        if (attachmentContent && !attachmentContent.startsWith('data:') && !attachmentContent.startsWith('http') && !attachmentContent.startsWith('blob:')) {
          // If it's base64 without data URL prefix, add it
          imageSrc = `data:${mimeType};base64,${attachmentContent}`;
        }
        
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <img 
              src={imageSrc}
              alt={selectedAttachment.originalFileName || selectedAttachment.name}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '70vh', 
                transform: `scale(${scale})`,
                transition: 'transform 0.2s ease'
              }}
              onError={(e) => {
                console.error('Error loading image');
                // Handle image load error
              }}
            />
          </Box>
        );
      } else {
        return (
          <Box sx={noAttachmentMessage}>
            <Typography variant="h3" sx={attachmentIcon}>
              {getFileTypeIcon(selectedAttachment.originalFileName || selectedAttachment.name)}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {selectedAttachment.originalFileName || selectedAttachment.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              לא ניתן להציג את סוג הקובץ הזה בדפדפן
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => handleDownloadAttachment(selectedAttachment)}
              startIcon={<Download />}
            >
              הורד קובץ
            </Button>
          </Box>
        );
      }
    }
  };

  return (
    <Container maxWidth={isFullscreen ? "xl" : "lg"} sx={{ py: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Paper elevation={0} sx={paperMain(isMobile)}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          gap: isMobile ? 2 : 0 
        }}>
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="500" color="text.primary" sx={{ mb: 1 }}>
              צרופות חשבונית {invoice?.booknum}
            </Typography>
            <Typography variant={isMobile ? "body2" : "body2"} color="text.secondary">
              צפייה והורדת צרופות החשבונית
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'row' : 'row', 
            gap: 2, 
            width: isMobile ? '100%' : 'auto' 
          }}>
            <Button 
              variant="outlined" 
              onClick={handleBackToInvoice} 
              sx={{ 
                borderRadius: 2, 
                borderColor: '#e0e0e0', 
                color: 'text.primary', 
                flex: isMobile ? 1 : 'none', 
                minHeight: isMobile ? 44 : 'auto',
                '&:hover': { 
                  borderColor: '#d0d0d0', 
                  backgroundColor: '#f8f9fa' 
                } 
              }} 
              startIcon={<Visibility />}
            >
              {isMobile ? 'לחשבונית' : 'חזרה לחשבונית'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleBackToSystem} 
              sx={{ 
                borderRadius: 2, 
                borderColor: '#e0e0e0', 
                color: 'text.primary', 
                flex: isMobile ? 1 : 'none', 
                minHeight: isMobile ? 44 : 'auto',
                '&:hover': { 
                  borderColor: '#d0d0d0', 
                  backgroundColor: '#f8f9fa' 
                } 
              }} 
              startIcon={<ArrowBack />}
            >
              {isMobile ? 'חזרה' : 'חזרה למערכת'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Invoice Info */}
      {invoice && (
        <Paper elevation={0} sx={paperInfo(isMobile)}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            flexWrap: 'wrap', 
            gap: isMobile ? 2 : 4, 
            alignItems: 'flex-start' 
          }}>
            <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                ספק
              </Typography>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {invoice.supName}
              </Typography>
            </Box>
            <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                תאריך
              </Typography>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {invoice.ivDate}
              </Typography>
            </Box>
            <Box sx={{ minWidth: isMobile ? '100%' : 'auto' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                כמות צרופות
              </Typography>
              <Typography variant="h6" fontWeight="600" color="text.primary">
                {attachments.length}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={isMobile ? 2 : 4}>
        {/* Attachments List */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={paperAttachments(isMobile)}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFile />
                צרופות ({attachments.length})
              </Typography>
            </Box>
            
            {attachments.length === 0 ? (
              <Box sx={noAttachmentMessage}>
                <Typography variant="h3" sx={attachmentIcon}>
                  📎
                </Typography>
                <Typography variant="h6" gutterBottom>
                  אין צרופות
                </Typography>
                <Typography variant="body2">
                  לא נמצאו צרופות לחשבונית זו
                </Typography>
              </Box>
            ) : (
              <List sx={attachmentsList}>
                {attachments.map((attachment, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSelectAttachment(attachment)}
                    sx={attachmentItem(selectedAttachment === attachment)}
                  >
                    <ListItemIcon sx={{ minWidth: '40px', color: 'inherit' }}>
                      <Typography variant="h6">
                        {getFileTypeIcon(attachment.originalFileName || attachment.name)}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="500" noWrap>
                            {attachment.originalFileName || attachment.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={formatFileSize(attachment.size)} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                            {isPdfFile(attachment.originalFileName || attachment.name) && (
                              <Chip 
                                label="PDF" 
                                size="small" 
                                color="primary"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAttachment(attachment);
                      }}
                      sx={{ color: 'inherit' }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Attachment Viewer */}
        <Grid item xs={12} md={8}>
          {selectedAttachment && (
            <Paper elevation={0} sx={attachmentViewer}>
              <Box sx={viewerToolbar}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="הקטן">
                    <IconButton onClick={handleZoomOut} disabled={scale <= 0.5}>
                      <ZoomOut />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                    {Math.round(scale * 100)}%
                  </Typography>
                  <Tooltip title="הגדל">
                    <IconButton onClick={handleZoomIn} disabled={scale >= 3.0}>
                      <ZoomIn />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    {selectedAttachment.originalFileName || selectedAttachment.name}
                  </Typography>
                  
                  {numPages && isPdfFile(selectedAttachment.originalFileName || selectedAttachment.name) && (
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      עמוד {pageNumber} מתוך {numPages}
                    </Typography>
                  )}
                  
                  <Tooltip title="הורד קובץ">
                    <IconButton onClick={() => handleDownloadAttachment(selectedAttachment)}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title={isFullscreen ? "יציאה ממסך מלא" : "מסך מלא"}>
                    <IconButton onClick={() => toggleFullscreen(isMobile)}>
                      {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              <Box className="attachment-container" sx={viewerContainer(isFullscreen)}>
                {renderAttachmentViewer()}
              </Box>
              
              {numPages > 1 && isPdfFile(selectedAttachment.originalFileName || selectedAttachment.name) && (
                <Box sx={viewerPager}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    disabled={pageNumber <= 1} 
                    onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))} 
                    sx={{ borderRadius: 2 }}
                  >
                    עמוד קודם
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    disabled={pageNumber >= numPages} 
                    onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))} 
                    sx={{ borderRadius: 2 }}
                  >
                    עמוד הבא
                  </Button>
                </Box>
              )}
            </Paper>
          )}
          
          {!selectedAttachment && (
            <Box sx={noAttachmentMessage}>
              <Typography variant="h3" sx={attachmentIcon}>
                📎
              </Typography>
              <Typography variant="h6" gutterBottom>
                בחר צרופה לצפייה
              </Typography>
              <Typography variant="body2">
                בחר אחת מהצרופות מהרשימה לצפייה והורדה
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ViewAttachments;